import { type HTMLAttributes, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { PageHeader } from '@/components/PageHeader';
import { formatPhone } from '@/features/customers/customerForm';
import type { CustomerAccountStatus } from '@/features/customers/types';
import {
  getBranchOptions,
  getContactSettings,
  getFollowUp,
  registerQuotationContact,
} from '@/features/operations/api';
import { formatHistoryTimestamp, getNoteAuthorLabel } from '@/features/operations/labels';
import type {
  BranchOption,
  FollowUpGroup,
  FollowUpPage as FollowUpBlockPage,
  FollowUpView,
  NoteEntry,
} from '@/features/operations/types';
import { buildWhatsAppUrl, isWhatsAppReachable } from '@/features/operations/whatsapp';
import { StatusChip } from '@/components/StatusChip';

const DAY_MS = 24 * 60 * 60 * 1000;

/// The two tabs, which are the issue's filter by group. Quotations first, as the issue lists
/// them; the URL names the tab only when it is not the default.
const tabs: { value: FollowUpGroup; label: string }[] = [
  { value: 'QUOTATIONS', label: 'Cotizaciones sin novedad' },
  { value: 'RESERVATIONS', label: 'Reservas activas' },
];

const tabId = (group: FollowUpGroup) => `follow-up-tab-${group.toLowerCase()}`;
const panelId = (group: FollowUpGroup) => `follow-up-panel-${group.toLowerCase()}`;

/// The page sizes the backend accepts, 20 unless the URL says otherwise.
const pageSizeOptions = [10, 20, 50];
const defaultPageSize = 20;

function readPage(value: string | null): number {
  const page = Number(value);
  return Number.isInteger(page) && page > 1 ? page : 1;
}

function readPageSize(value: string | null): number {
  const pageSize = Number(value);
  return pageSizeOptions.includes(pageSize) ? pageSize : defaultPageSize;
}

/**
 * The pages of one block, with how many rows to show. MUI's TablePagination and not the
 * `Pagination` of the customer list, because this one also picks the page size.
 */
function BlockPagination({
  block,
  label,
  onPageChange,
  onPageSizeChange,
}: {
  block: FollowUpBlockPage<unknown>;
  label: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}) {
  return (
    <TablePagination
      component="div"
      count={block.total}
      // MUI counts pages from 0, the backend from 1.
      page={block.page - 1}
      rowsPerPage={block.pageSize}
      rowsPerPageOptions={pageSizeOptions}
      onPageChange={(_event, page) => onPageChange(page + 1)}
      onRowsPerPageChange={(event) => onPageSizeChange(Number(event.target.value))}
      labelRowsPerPage="Filas por página"
      labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
      getItemAriaLabel={(type) =>
        type === 'next'
          ? `Página siguiente de ${label}`
          : type === 'previous'
            ? `Página anterior de ${label}`
            : type === 'first'
              ? `Primera página de ${label}`
              : `Última página de ${label}`
      }
      // Both blocks have this select: its name says which block, instead of the shared label.
      SelectProps={{
        SelectDisplayProps: {
          'aria-label': `Filas por página de ${label}`,
          'aria-labelledby': undefined,
        } as HTMLAttributes<HTMLDivElement>,
      }}
    />
  );
}

function isGroup(value: string | null): value is FollowUpGroup {
  return value === 'QUOTATIONS' || value === 'RESERVATIONS';
}

/// "Hoy", "1 día", "4 días": whole days of 24 hours, as the backend counts them.
function daysLabel(days: number): string {
  if (days <= 0) return 'Hoy';
  return days === 1 ? '1 día' : `${days} días`;
}

/// The same reading as the reservation block of the device sheet (#225).
function expiryLabel(expiresAt: string, now: Date): string {
  const days = Math.ceil((new Date(expiresAt).getTime() - now.getTime()) / DAY_MS);
  if (days <= 1) return 'Vence en menos de un día';
  return `Quedan ${days} días`;
}

function deviceLabel(device: { brand: string; model: string; storageGb: number | null }) {
  return `${device.brand} ${device.model}${device.storageGb === null ? '' : ` ${device.storageGb}GB`}`;
}

function LastNoteCell({ note }: { note: NoteEntry | null }) {
  if (!note) {
    return (
      <Typography variant="body2" color="text.secondary">
        Sin notas
      </Typography>
    );
  }

  return (
    <Box sx={{ maxWidth: 320 }}>
      {/* Two lines at most: the full note is in the detail, one click away. */}
      <Typography
        variant="body2"
        title={note.body}
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          overflowWrap: 'anywhere',
        }}
      >
        {note.body}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {getNoteAuthorLabel(note)} · {formatHistoryTimestamp(note.createdAt)}
      </Typography>
    </Box>
  );
}

function CustomerCell({
  customer,
  contact,
}: {
  customer: { id: string; fullName: string; status: CustomerAccountStatus } | null;
  contact: { name: string; phone: string };
}) {
  return (
    <Stack spacing={0.5} alignItems="flex-start">
      {customer ? (
        <Link
          component={RouterLink}
          to={`/admin/customers/${customer.id}`}
          underline="hover"
          color="inherit"
          fontWeight={600}
          sx={{ overflowWrap: 'anywhere' }}
        >
          {contact.name}
        </Link>
      ) : (
        <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
          {contact.name}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {formatPhone(contact.phone)}
      </Typography>
      {customer ? <StatusChip domain="customerAccount" status={customer.status} /> : null}
    </Stack>
  );
}

/**
 * Why WhatsApp cannot open for this row, told apart the way the quotation detail does: the
 * employee needs to know whether the number or the configuration is the problem.
 */
function whatsAppBlockedReason(phone: string, hasMessage: boolean): string | null {
  if (!phone.trim() || phone === 'No informado') return 'No hay teléfono registrado.';
  if (!isWhatsAppReachable(phone)) return 'El teléfono no está en formato internacional.';
  if (!hasMessage) return 'Configurá el mensaje inicial en Administración → Cotización.';
  return null;
}

function WhatsAppAction({
  phone,
  message,
  label,
  isRegistering = false,
  onOpen,
}: {
  phone: string;
  message: string | null;
  label: string;
  isRegistering?: boolean;
  onOpen?: () => void;
}) {
  const blockedReason = whatsAppBlockedReason(phone, Boolean(message));

  if (blockedReason) {
    return (
      <Box>
        <Button variant="outlined" size="small" disabled aria-label={label}>
          WhatsApp
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {blockedReason}
        </Typography>
      </Box>
    );
  }

  return (
    // A real anchor: the click itself opens the chat, so no popup blocker can cut it, and
    // recording the contact runs alongside instead of gating it.
    <Button
      variant="outlined"
      size="small"
      component="a"
      href={buildWhatsAppUrl(phone, message || '')}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      disabled={isRegistering}
      onClick={onOpen}
    >
      {isRegistering ? 'Registrando…' : 'WhatsApp'}
    </Button>
  );
}

/**
 * Who PhoneTec has to contact today (CRM-7, #111): quoted requests whose phone never came in and
 * that had no news for more than three days, and active reservations, the soonest to end first.
 *
 * A view derived on every read, without a state of its own (ADR 0005). It reloads after a
 * contact is registered, and every time it is opened, so a new note or status shows on arrival.
 *
 * One tab per group. Both are read on every load, so each tab shows its total and switching is
 * immediate. The tab, the branch, and each tab's page and page size live in the URL, as the
 * quotation list keeps its filters, so coming back from a detail lands on the same place.
 */
export function FollowUpPage() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const groupParam = searchParams.get('group');
  const tab: FollowUpGroup = isGroup(groupParam) ? groupParam : 'QUOTATIONS';
  const branchId = searchParams.get('branchId') || '';
  const quotationsPage = readPage(searchParams.get('quotationsPage'));
  const quotationsPageSize = readPageSize(searchParams.get('quotationsPageSize'));
  const reservationsPage = readPage(searchParams.get('reservationsPage'));
  const reservationsPageSize = readPageSize(searchParams.get('reservationsPageSize'));
  const [view, setView] = useState<FollowUpView | null>(null);
  const [branches, setBranches] = useState<BranchOption[]>([]);
  const [whatsAppMessage, setWhatsAppMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [registeringId, setRegisteringId] = useState<string | null>(null);
  const now = new Date();

  /// Values equal to the default stay out of the URL, so a plain link is the default view.
  const setParams = (updates: Record<string, string>) =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(updates)) {
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
        }
        return next;
      },
      { replace: true },
    );

  /// A new branch changes what the reservations hold, so they go back to their first page, as
  /// in the customer list. The page size is kept: it is a preference, not a filter.
  const setBranch = (value: string) => setParams({ branchId: value, reservationsPage: '' });

  /// Switching tabs keeps every other place: the branch, and each tab's page.
  const setTab = (value: FollowUpGroup) =>
    setParams({ group: value === 'QUOTATIONS' ? '' : value });

  const pageParam = (page: number) => (page > 1 ? String(page) : '');
  const pageSizeParam = (pageSize: number) =>
    pageSize === defaultPageSize ? '' : String(pageSize);

  const loadView = useCallback(async () => {
    if (!token) return;

    setIsLoading(true);
    try {
      setView(
        await getFollowUp(token, {
          branchId: branchId || undefined,
          quotationsPage,
          quotationsPageSize,
          reservationsPage,
          reservationsPageSize,
        }),
      );
    } catch {
      setErrorMessage('No pudimos cargar el seguimiento. Probá de nuevo en un momento.');
    } finally {
      setIsLoading(false);
    }
  }, [token, branchId, quotationsPage, quotationsPageSize, reservationsPage, reservationsPageSize]);

  useEffect(() => {
    setErrorMessage(null);
    void loadView();
  }, [loadView]);

  // What the filter and the WhatsApp button need. Either failing leaves the view usable: the
  // filter shows only "Todas", and the button says the message is missing.
  useEffect(() => {
    if (!token) return;

    getBranchOptions(token)
      .then(setBranches)
      .catch(() => setBranches([]));
    getContactSettings(token)
      .then((settings) => setWhatsAppMessage(settings?.whatsappMessageTemplate || null))
      .catch(() => setWhatsAppMessage(null));
  }, [token]);

  /// The chat already opened; this leaves it on file and reloads, so the request, which now has
  /// news, leaves the block.
  const handleQuotationContact = async (quotationId: string, name: string) => {
    if (!token) return;

    setRegisteringId(quotationId);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await registerQuotationContact(token, quotationId, 'WHATSAPP');
      setSuccessMessage(
        `Contacto con ${name} registrado. La cotización sale del seguimiento hasta que vuelva a quedar sin novedad.`,
      );
      await loadView();
    } catch {
      setErrorMessage(
        `Se abrió WhatsApp, pero no pudimos registrar el contacto con ${name}. Dejalo anotado en la cotización.`,
      );
    } finally {
      setRegisteringId(null);
    }
  };

  if (!token) {
    return null;
  }

  const staleAfterDays = view?.staleAfterDays ?? 3;
  const selectedBranch = branches.find((branch) => branch.id === branchId);

  return (
    <Stack spacing={4}>
      <PageHeader
        eyebrow="SEGUIMIENTO"
        title="Seguimiento comercial"
        description="A quién hay que contactar hoy: cotizaciones que no avanzan y reservas activas."
        badge="Uso interno"
      />

      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

      {isLoading && !view ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
          <CircularProgress color="secondary" />
        </Box>
      ) : null}

      {view?.quotations && view.reservations ? (
        <Card>
          <Tabs
            value={tab}
            onChange={(_event, value: FollowUpGroup) => setTab(value)}
            aria-label="Grupos del seguimiento"
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 1, sm: 2 } }}
          >
            {tabs.map((option) => (
              <Tab
                key={option.value}
                value={option.value}
                id={tabId(option.value)}
                aria-controls={panelId(option.value)}
                label={`${option.label} (${
                  option.value === 'QUOTATIONS' ? view.quotations?.total : view.reservations?.total
                })`}
              />
            ))}
          </Tabs>
          {/* Reloading after a page, a branch or a contact: the previous rows stay, so the page
              does not jump, but they are marked as outdated until the new answer arrives. */}
          {isLoading ? (
            <LinearProgress color="secondary" aria-label="Actualizando el seguimiento" />
          ) : null}

          <CardContent>
            {tab === 'QUOTATIONS' ? (
              <Stack
                spacing={2.5}
                role="tabpanel"
                id={panelId('QUOTATIONS')}
                aria-labelledby={tabId('QUOTATIONS')}
              >
                <Typography variant="body2" color="text.secondary">
                  Cotizadas, con el equipo todavía sin recibir y sin novedad hace más de{' '}
                  {staleAfterDays} días. Las más olvidadas, primero. Una nota o un contacto las saca
                  de acá.
                </Typography>
                {view.quotations.total === 0 ? (
                  <Alert severity="info">
                    No hay cotizaciones sin novedad hace más de {staleAfterDays} días. Las que
                    tienen una nota o un contacto reciente no aparecen.
                  </Alert>
                ) : (
                  <Box>
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small" aria-label="Cotizaciones sin novedad">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ minWidth: 180 }}>Cliente</TableCell>
                            <TableCell sx={{ minWidth: 150 }}>Modelo</TableCell>
                            <TableCell sx={{ minWidth: 220 }}>Última nota</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>Sin novedad</TableCell>
                            <TableCell sx={{ minWidth: 160 }}>Acciones</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {view.quotations.items.map((quotation) => (
                            <TableRow key={quotation.id}>
                              <TableCell>
                                <CustomerCell
                                  customer={quotation.customer}
                                  contact={quotation.contact}
                                />
                              </TableCell>
                              <TableCell>{deviceLabel(quotation.device)}</TableCell>
                              <TableCell>
                                <LastNoteCell note={quotation.lastNote} />
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {daysLabel(quotation.daysWithoutActivity)}
                              </TableCell>
                              <TableCell>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="flex-start"
                                  flexWrap="wrap"
                                >
                                  <WhatsAppAction
                                    phone={quotation.contact.phone}
                                    message={whatsAppMessage}
                                    label={`Contactar a ${quotation.contact.name} por WhatsApp`}
                                    isRegistering={registeringId === quotation.id}
                                    onOpen={() =>
                                      void handleQuotationContact(
                                        quotation.id,
                                        quotation.contact.name,
                                      )
                                    }
                                  />
                                  <Button
                                    size="small"
                                    component={RouterLink}
                                    to={`/admin/quotations/${quotation.id}`}
                                    aria-label={`Ver la cotización de ${quotation.contact.name}`}
                                  >
                                    Ver cotización
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                    <BlockPagination
                      block={view.quotations}
                      label="cotizaciones sin novedad"
                      onPageChange={(page) => setParams({ quotationsPage: pageParam(page) })}
                      onPageSizeChange={(pageSize) =>
                        setParams({
                          quotationsPageSize: pageSizeParam(pageSize),
                          quotationsPage: '',
                        })
                      }
                    />
                  </Box>
                )}
              </Stack>
            ) : (
              <Stack
                spacing={2.5}
                role="tabpanel"
                id={panelId('RESERVATIONS')}
                aria-labelledby={tabId('RESERVATIONS')}
              >
                <Typography variant="body2" color="text.secondary">
                  Las que vencen antes, primero. Una reserva vencida sigue reservando el equipo
                  hasta que alguien la cancele.
                </Typography>
                {/* Only reservations have a branch, through their device: the filter lives in
                    their tab, and stays in the URL while the other tab is open. */}
                <Grid container>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      select
                      label="Sucursal"
                      value={branchId}
                      onChange={(event) => setBranch(event.target.value)}
                      helperText="La sucursal donde está el equipo reservado."
                    >
                      <MenuItem value="">Todas</MenuItem>
                      {branches.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id}>
                          {branch.name}
                          {branch.isActive ? '' : ' (inactiva)'}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
                {view.reservations.total === 0 ? (
                  <Alert severity="info">
                    {selectedBranch
                      ? `No hay reservas activas de equipos en ${selectedBranch.name}.`
                      : 'No hay reservas activas. Se reserva desde la ficha de un equipo publicado.'}
                  </Alert>
                ) : (
                  <Box>
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small" aria-label="Reservas activas">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ minWidth: 150 }}>Vence</TableCell>
                            <TableCell sx={{ minWidth: 180 }}>Comprador</TableCell>
                            <TableCell sx={{ minWidth: 170 }}>Equipo</TableCell>
                            <TableCell sx={{ minWidth: 220 }}>Última nota</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>Sin novedad</TableCell>
                            <TableCell sx={{ minWidth: 160 }}>Acciones</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {view.reservations.items.map((reservation) => (
                            <TableRow key={reservation.id}>
                              <TableCell>
                                <Stack spacing={0.5} alignItems="flex-start">
                                  <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                    {formatHistoryTimestamp(reservation.expiresAt)}
                                  </Typography>
                                  {reservation.isExpired ? (
                                    <StatusChip domain="reservation" status="EXPIRED" />
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">
                                      {expiryLabel(reservation.expiresAt, now)}
                                    </Typography>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <CustomerCell
                                  customer={reservation.customer}
                                  contact={reservation.contact}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {deviceLabel(reservation.device)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" component="p">
                                  {reservation.device.branch?.name ?? 'Sin sucursal'} · Reserva #
                                  {reservation.reference}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <LastNoteCell note={reservation.lastNote} />
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {daysLabel(reservation.daysWithoutActivity)}
                              </TableCell>
                              <TableCell>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="flex-start"
                                  flexWrap="wrap"
                                >
                                  <WhatsAppAction
                                    phone={reservation.contact.phone}
                                    message={whatsAppMessage}
                                    label={`Contactar a ${reservation.contact.name} por WhatsApp`}
                                  />
                                  <Button
                                    size="small"
                                    component={RouterLink}
                                    to={`/admin/devices/${reservation.device.id}`}
                                    aria-label={`Ver el equipo reservado por ${reservation.contact.name}`}
                                  >
                                    Ver equipo
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                    <BlockPagination
                      block={view.reservations}
                      label="reservas activas"
                      onPageChange={(page) => setParams({ reservationsPage: pageParam(page) })}
                      onPageSizeChange={(pageSize) =>
                        setParams({
                          reservationsPageSize: pageSizeParam(pageSize),
                          reservationsPage: '',
                        })
                      }
                    />
                  </Box>
                )}
              </Stack>
            )}
          </CardContent>
        </Card>
      ) : null}
    </Stack>
  );
}
